import Bugsnag from '@bugsnag/js'
import {
  Alert,
  Box,
  CircularProgress,
  Grid,
  Typography,
  useTheme,
} from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { BsCheck } from 'react-icons/bs'
// Components
import { Formtitle, SigningFrame } from '../../components'
import SignedBiDownloadButton from '../../components/iperia/SignedBiDownloadButton'
// Types
import type { IperiaResignature, IperiaWorkflowStatus } from '../../types/iperiaStatus'
// Utils
import { client } from '../../utils/client'
import { useBrand } from '../../utils/brand'
import { downloadParentSignedBi } from '../../utils/iperiaSignedBi'
import IperiaResignatureNotice from '../../components/iperia/IperiaResignatureNotice'

type ParentStatus = {
  workflow_status: IperiaWorkflowStatus
  bi_pe_signed_at: string | null
  signing_url: string | null
  document_preparing?: boolean
  resignature?: IperiaResignature | null
}

const PARENT_STATUS_POLL_MS = 3000

const ParentValidation = () => {
  const theme = useTheme()
  const brand = useBrand()
  // The token is a JWT: its dots would make the last path segment look like a static
  // file, so it travels as a query parameter rather than a route param.
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token') || ''
  const [status, setStatus] = useState<ParentStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [documentPreparing, setDocumentPreparing] = useState(false)
  const [externalSigningOpen, setExternalSigningOpen] = useState(false)

  const refetch = useCallback(async () => {
    try {
      const response = await client.get('/registration/iperia/parent_status/', { params: { token } })
      const parentStatus = response.data as ParentStatus
      setStatus(parentStatus)
      setDocumentPreparing(Boolean(parentStatus.document_preparing) || !parentStatus.signing_url)
      sessionStorage.setItem('iperia_signer', 'parent')
      sessionStorage.setItem('iperia_parent_token', token)
      setError(false)
    } catch (err) {
      const axiosErr = err as { response?: { status?: number; data?: { document_preparing?: boolean } } }
      // 409 while the AM signature webhook / PE signing URL is still catching up.
      if (axiosErr.response?.status === 409) {
        setDocumentPreparing(true)
        setError(false)
        setStatus((prev) => prev ?? {
          workflow_status: 'awaiting_pe_signature',
          bi_pe_signed_at: null,
          signing_url: null,
          document_preparing: true,
        })
      } else {
        Bugsnag.notify(err as Error)
        setError(true)
        setDocumentPreparing(false)
      }
    } finally {
      setLoading(false)
    }
  }, [token])

  useEffect(() => {
    void refetch()
  }, [refetch])

  useEffect(() => {
    if (!documentPreparing || status?.signing_url || status?.bi_pe_signed_at) return undefined
    const intervalId = setInterval(() => {
      refetch().catch((err: Error) => Bugsnag.notify(err))
    }, PARENT_STATUS_POLL_MS)
    return () => clearInterval(intervalId)
  }, [documentPreparing, status?.signing_url, status?.bi_pe_signed_at, refetch])

  useEffect(() => {
    if (!externalSigningOpen || status?.bi_pe_signed_at) return undefined
    const intervalId = setInterval(() => {
      refetch().catch((err: Error) => Bugsnag.notify(err))
    }, PARENT_STATUS_POLL_MS)
    return () => clearInterval(intervalId)
  }, [externalSigningOpen, status?.bi_pe_signed_at, refetch])

  useEffect(() => {
    if (!status?.bi_pe_signed_at) return
    if (window.location.hash === '#success') return
    navigate({ search: searchParams.toString(), hash: 'success' }, { replace: true })
  }, [status?.bi_pe_signed_at, navigate, searchParams])

  useEffect(() => {
    const listener = (event: MessageEvent) => {
      if (event.origin === window.location.origin && event.data === 'DOCUSIGN_SIGNED_REFRESH') {
        refetch().catch((err: Error) => Bugsnag.notify(err))
      }
    }
    window.addEventListener('message', listener)
    return () => window.removeEventListener('message', listener)
  }, [refetch])

  if (loading && !documentPreparing) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
  }
  if (error || (!status && !documentPreparing) || !token) {
    return <Alert severity="error">Ce lien est invalide ou a expiré.</Alert>
  }

  if (status?.bi_pe_signed_at) {
    return (
      <Grid container alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
        <Grid item xs={12}>
          <Grid container alignItems="center" justifyContent="center">
            <Grid
              item
              sx={{
                background: theme.palette.success.main,
                height: 80,
                width: 80,
                borderRadius: 40,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              <BsCheck size="40px" style={{ color: 'white' }} />
            </Grid>
          </Grid>
          <Formtitle
            title="Inscription finalisée"
            align="center"
          />
          <Typography variant="body1" align="center" sx={{ marginTop: 6 }}>
            Le document a été signé par les deux parties.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <SignedBiDownloadButton onDownload={() => downloadParentSignedBi(token)} />
          </Box>
          <Typography variant="body2" align="center" sx={{ marginTop: 2, color: theme.palette.text.secondary }}>
            {brand.name} — {brand.footerTagline}
          </Typography>
        </Grid>
      </Grid>
    )
  }

  if (documentPreparing || !status?.signing_url) {
    return (
      <Box sx={{ maxWidth: 900, mx: 'auto', p: { xs: 2, sm: 4 } }}>
        <Formtitle
          title="Signature du bulletin d'inscription"
          subtitle="Préparation du document…"
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6 }}>
          <CircularProgress />
          <Typography sx={{ mt: 2, textAlign: 'center' }}>
            Le document est en cours de génération
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, textAlign: 'center', color: 'text.secondary' }}>
            Merci de patienter, la page se mettra à jour automatiquement.
          </Typography>
        </Box>
      </Box>
    )
  }

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: { xs: 2, sm: 4 } }}>
      <Formtitle
        title="Signature du bulletin d'inscription"
        subtitle="Signez le bulletin d'inscription pour finaliser le dossier."
      />

      <Box sx={{ mt: 2 }}>
        <IperiaResignatureNotice resignature={status.resignature} />
      </Box>

      <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
        Lors de la signature, vous serez invité(e) à vérifier ou renseigner la date de naissance
        ainsi que la date de signature.
      </Typography>

      <Box sx={{ mt: 3 }}>
        <SigningFrame
          src={status.signing_url}
          title="Signature du parent employeur"
          height="min(680px, calc(100dvh - 220px))"
          onOpened={() => setExternalSigningOpen(true)}
        />
      </Box>
    </Box>
  )
}

export default ParentValidation
